import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const VirtualScienceLabGame = ({ gameData, onComplete }) => {
    const [currentExperimentIndex, setCurrentExperimentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [feedback, setFeedback] = useState(null); 

    const totalExperiments = gameData?.experiments?.length || 0;
    const currentExperiment = gameData?.experiments?.[currentExperimentIndex];
    const pointsPerExperiment = gameData?.points_per_experiment || 20;

    const getExperimentIcon = (type) => {
        switch (type) {
            case 'color_mix': return '🎨';
            case 'weight': return '⚖️';
            case 'temperature': return '🌡️';
            case 'chemical': return '🧪';
            case 'electricity': return '⚡';
            default: return '🔬';
        }
    };

    const handleOptionSelect = (option) => {
        if (selectedOption || feedback) return;

        triggerHaptic(30);
        setSelectedOption(option);
        const isCorrect = option === currentExperiment.correct_answer;

        if (isCorrect) {
            const newScore = score + pointsPerExperiment;
            setScore(newScore);
            setFeedback({ status: 'correct', experiment: currentExperiment });
            triggerHaptic([100, 50, 100]);
        } else {
            setFeedback({ status: 'wrong', experiment: currentExperiment });
            triggerHaptic([50, 50, 50]);
        }

        setTimeout(() => {
            setFeedback(null);
            setSelectedOption(null);

            if (currentExperimentIndex < totalExperiments - 1) {
                setCurrentExperimentIndex(prev => prev + 1);
                triggerHaptic(30);
            } else {
                triggerHaptic([200, 100, 200, 100, 300]);
                onComplete?.(score + (isCorrect ? pointsPerExperiment : 0));
            }
        }, 6000);
    };

    if (!gameData || !gameData.experiments || !currentExperiment) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-emerald-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🧪</div>
                <h3 className="text-base md:text-3xl font-black text-emerald-900 tracking-tighter uppercase">Ruošiami reagentai...</h3>
            </div>
        );
    }

    const progress = ((currentExperimentIndex + 1) / totalExperiments) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-emerald-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-20 md:h-20 bg-emerald-100 rounded-xl md:rounded-3xl flex items-center justify-center text-xl md:text-4xl shadow-inner border-2 border-emerald-200 animate-float">
                        🔬
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Mokslo <span className="text-emerald-600">Laboratorija</span>
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">IQ Taškai</p>
                        <p className="text-sm md:text-2xl font-black text-emerald-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Tyrimas</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentExperimentIndex + 1}/{totalExperiments}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-emerald-400 via-teal-500 to-emerald-700 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(16,185,129,0.4)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8 flex flex-col items-center">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center w-full max-w-4xl text-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-[150px] opacity-[0.03] select-none pointer-events-none rotate-12">🧬</div>
                
                <div className="space-y-4 md:space-y-8 relative z-10 w-full">
                    <div className="w-24 h-24 md:w-40 md:h-40 bg-white rounded-xl md:rounded-[2.5rem] shadow-xl border-4 md:border-8 border-emerald-50 flex items-center justify-center text-4xl md:text-8xl mx-auto animate-float">
                        {getExperimentIcon(currentExperiment.type)}
                    </div>
                    <div className="space-y-2 md:space-y-4">
                        <h4 className="text-[8px] md:text-[10px] font-black text-emerald-500 uppercase tracking-[0.5em] mb-2">Tyrimo Klausimas</h4>
                        <h2 className="text-xl md:text-5xl font-black text-slate-900 tracking-tighter leading-tight italic font-serif">
                            {currentExperiment.question}
                        </h2>
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6 w-full max-w-5xl mx-auto py-4">
            {currentExperiment.options.map((option, index) => {
                const isSelected = selectedOption === option;
                const isCorrect = option === currentExperiment.correct_answer;
                
                return (
                    <button
                        key={index}
                        onClick={() => handleOptionSelect(option)}
                        disabled={!!selectedOption}
                        className={`
                            premium-button-3d relative p-4 md:p-8 rounded-xl md:rounded-[3rem] flex items-center gap-4 transition-all duration-500 border-b-8 md:border-b-[12px]
                            ${isSelected 
                                ? isCorrect 
                                    ? 'bg-emerald-500 text-white border-emerald-800 scale-105 shadow-2xl' 
                                    : 'bg-rose-500 text-white border-rose-800 animate-shake'
                                : selectedOption && isCorrect 
                                    ? 'bg-emerald-500 text-white border-emerald-800 scale-105 shadow-2xl' 
                                    : 'bg-white text-slate-800 border-slate-200 hover:bg-emerald-50 active:translate-y-2 active:border-b-0 shadow-xl'}
                            disabled:cursor-default
                        `}
                    >
                        <div className={`
                            w-10 h-10 md:w-16 md:h-16 rounded-lg md:rounded-2xl flex items-center justify-center text-sm md:text-2xl font-black shrink-0 border-2 md:border-4 transition-all
                            ${isSelected || (selectedOption && isCorrect) ? 'bg-white/20 border-white/40 text-white' : 'bg-slate-100 border-slate-200 text-slate-400'}
                        `}>
                            {String.fromCharCode(65 + index)}
                        </div>
                        <span className="flex-1 text-sm md:text-2xl font-black uppercase tracking-tighter text-left italic font-serif leading-tight">{option}</span>
                    </button>
                );
            })}
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-3xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[5rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px] text-center
                        ${feedback.status === 'correct' ? 'border-emerald-500' : 'border-blue-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback.status === 'correct' ? '🧪' : '🔬'}
                        </div>
                        
                        <div className="text-center space-y-6 pt-4">
                            <h2 className={`text-2xl md:text-6xl font-black uppercase tracking-tighter italic font-serif ${feedback.status === 'correct' ? 'text-emerald-600' : 'text-blue-600'}`}>
                                {feedback.status === 'correct' ? 'TYRIMAS SĖKMINGAS!' : 'MOKSLINIS PAŽINIMAS'}
                            </h2>

                            <div className={`p-6 md:p-10 rounded-2xl md:rounded-[3rem] border-4 text-center italic font-serif leading-relaxed ${feedback.status === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-blue-50 border-blue-100'}`}>
                                <p className="text-sm md:text-2xl font-bold text-slate-800 leading-relaxed italic">
                                    "{feedback.experiment.explanation}"
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default VirtualScienceLabGame;
